import React, { useCallback, useEffect, useRef, useState } from 'react';
import EvaluationExperienceHeader from './Header/evaluationExperienceHeader';
import styles from './evaluationExperience.module.css';
import EvaluationTaskTicket from './Ticket/evaluationTaskTicket';
import { LoadingSpinner } from '@qualtrics/ui-react';
import { ConfigPane } from '@qualtrics/ui-react/next';
import { 
    getSurveyLinkUrl
} from '../services/evaluationPageService';
import { 
    getCourseCustomText,
    getTeacherCustomText 
} from '../services/customTextService';
import InitialRetakeScreen from './InitialRetakeScreen/initialRetakeScreen';
import CompletedPage from './CompletedPage/completedPage';
import CustomTextDisplay from './CustomTextDisplay/customTextDisplay';

const language = document.getElementById('language').textContent;
const channel = document.getElementById('channel').textContent;
const hashedStudentId = document.getElementById('uId').textContent;

const EvaluationExperience = ({
    evaluationPage,
    evaluationTaskGroup,
    areRetakesEnabled,
    isCustomTextEnabled,
    brandId,
    onSurveySubmission,
    evaluations,
    setSelectedEvaluation
}) => {
    const TEACHER_CHOSEN_QUESTIONS_EVAL_TYPE = 'TEACHER_CHOSEN_QUESTIONS';
    const COURSE_EVAL_TYPE = 'COURSE';
    const TEACHER_EVAL_TYPE = 'TEACHER';
    // 'itesm' and 'educacioncontinuatec' requested for the config pane to be open by defaulton desktop: 
    // https://qualtrics.atlassian.net/browse/ES-130989?atlOrigin=eyJpIjoiOWUyMzdkNDM4MjFiNDNjNTllNWI3ODdlYzY2MDZkYWQiLCJwIjoiaiJ9
    const BRANDS_WITH_CONFIG_PANE_OPEN_AND_PINNED_BY_DEFAULT_ON_DESKTOP = ['itesm', 'courseevaldemo1', 'educacioncontinuatec'];

    const getInitialEvaluationTask = () => {
        return evaluationTaskGroup?.find((task) => !task.completed && !task.isBlocked)
            || evaluationTaskGroup?.find((task) => !task.isBlocked)
            || evaluationTaskGroup?.find((task) => !task.completed)
            || evaluationTaskGroup?.find(Boolean);
    };

    const MainContent = {
        SurveyPage: 'SurveyPage',
        IntitialRetakeScreen: 'InitialRetakeScreen',
        CompletedPage: 'CompletedPage'
    };

    const shouldConfigPageDefaultToOpenAndPinned = (
        BRANDS_WITH_CONFIG_PANE_OPEN_AND_PINNED_BY_DEFAULT_ON_DESKTOP.includes(brandId) && window.innerWidth > 440
    );

    const [selectedEvaluationTask, setSelectedEvaluationTask] = useState(getInitialEvaluationTask());
    const [surveyUrl, setSurveyUrl] = useState(undefined);
    const [currentMainContent, setCurrentMainContent] = useState(undefined);
    const [isLoadingSurveyIFrame, setIsLoadingSurveyIFrame] = useState(true);
    const [isConfigPaneOpen, setIsConfigPaneOpen] = useState(shouldConfigPageDefaultToOpenAndPinned);
    const [isConfigPanePinned, setIsConfigPanePinned] = useState(shouldConfigPageDefaultToOpenAndPinned);
    const [courseCustomText, setCourseCustomText] = useState(undefined);
    const [teacherCustomText, setTeacherCustomText] = useState(undefined);
    const [width, setWidth] = useState(window.innerWidth);
    const [isMobile, setIsMobile] = useState(false);
    const [containerStyle, setContainerStyle] = useState('container');
    const [isSubmissionTimeout, setIsSubmissionTimeout] = useState(false);
    
    const selectedEvaluationTaskRef = useRef();
    useEffect(() => {
        selectedEvaluationTaskRef.current = selectedEvaluationTask
    }, [selectedEvaluationTask])

    // Handle mobile device view
    const handleWindowSizeChange = () => {
        setWidth(window.innerWidth);
    }

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        }
    }, []);

    useEffect(() => {
        setIsMobile(width <= 440);
    }, [width]);

    useEffect(() => {
        setContainerStyle(isMobile? 'container-mobile' : 'container');
    }, [isMobile]);


    const configPaneIsOpenChangeHandler = (newIsOpen) => {
        setIsConfigPaneOpen(newIsOpen);
        setIsConfigPanePinned(false);
    };

    const getSurveyLink = async () => {
        if (!selectedEvaluationTask) {
            return;
        }

        try {
            setIsLoadingSurveyIFrame(true);
            if (!selectedEvaluationTask.isBlocked) {
                const newSurveyUrl = await getSurveyLinkUrl(
                    selectedEvaluationTask.transactionId,
                    selectedEvaluationTask.distributionId,
                    brandId,
                    language,
                    channel,
                    hashedStudentId
                );
                setSurveyUrl(newSurveyUrl);
            }
        } catch (e) {
            setSurveyUrl(undefined);
            setIsLoadingSurveyIFrame(false);
        }
    };

    const getCustomTextData = async () => {
        if(currentMainContent && currentMainContent !== MainContent.CompletedPage && isCustomTextEnabled) {  
            if(selectedEvaluationTask.evalType === COURSE_EVAL_TYPE) {
                const courseCustomTextData = await getCourseCustomText(brandId, selectedEvaluationTask.courseId);
                setCourseCustomText(courseCustomTextData);
            }
            if(selectedEvaluationTask.evalType === TEACHER_EVAL_TYPE || selectedEvaluationTask.evalType === TEACHER_CHOSEN_QUESTIONS_EVAL_TYPE) {
                const teacherCustomTextData = await getTeacherCustomText(brandId, selectedEvaluationTask.teacherId, selectedEvaluationTask.courseId);
                setTeacherCustomText(teacherCustomTextData);
            }
        }
    };

    useEffect(() => {
        if (!selectedEvaluationTask) {
            return;
        }

        if (selectedEvaluationTask.completed &&
            selectedEvaluationTask.evalType !== TEACHER_CHOSEN_QUESTIONS_EVAL_TYPE &&
            areRetakesEnabled) {
            setCurrentMainContent(MainContent.IntitialRetakeScreen);
            getSurveyLink();
        } else if (selectedEvaluationTask.completed &&
            selectedEvaluationTask.evalType !== TEACHER_CHOSEN_QUESTIONS_EVAL_TYPE) {
            setCurrentMainContent(MainContent.CompletedPage);
        } else {
            setCurrentMainContent(MainContent.SurveyPage);
            getSurveyLink();
        }
    }, [selectedEvaluationTask]);

    useEffect(() => {
        setSelectedEvaluationTask(getInitialEvaluationTask()); 
    }, [evaluationTaskGroup]);
    
    useEffect(() => {
        getCustomTextData();
    }, [currentMainContent, isCustomTextEnabled, selectedEvaluationTask]);

    useEffect(() => {
        if (isLoadingSurveyIFrame) return;
        const handleMessage = (event) => {
            // Listening for end of survey postMessage from JFE
            if (event.data && typeof event.data === 'string' && (
                event.data.includes('QualtricsEOS|') || 
                event.data.includes('endOfSurvey')
            )) {
                window.removeEventListener('message', handleMessage);
                setIsSubmissionTimeout(true);
                setTimeout(() => {
                    onSurveySubmission(selectedEvaluationTaskRef.current.transactionId);
                    setIsSubmissionTimeout(false);
                }, 1000)
            }
        }
        window.addEventListener('message', handleMessage)
        return () => {
            try {
                window.removeEventListener('message', handleMessage);
            } catch (e) {
                console.log(e, 'Failed to remove eventListener')
            }
        }
    }, [isLoadingSurveyIFrame])

    const iFrameOnLoadHandler = () => {
        if (surveyUrl === undefined || currentMainContent !== MainContent.SurveyPage) {
            return;
        }
        setIsLoadingSurveyIFrame(false);
    };

    const EvaluationConfigPane = () => {
        if (evaluationTaskGroup === undefined) {
            return <></>;
        }

        const formatEvaluationTaskGroup = useCallback(() => {
            // Adds teacher chosen questions evaluation task as a field to the main teacher evaluation task
            const teacherChosenQuestionsEvaluations = evaluationTaskGroup.filter((task) => task.evalType === TEACHER_CHOSEN_QUESTIONS_EVAL_TYPE);
            if (teacherChosenQuestionsEvaluations.length === 0) {
                return evaluationTaskGroup;
            }

            const nonTeacherChosenQuestionsEvaluations = evaluationTaskGroup.filter((task) => task.evalType !== TEACHER_CHOSEN_QUESTIONS_EVAL_TYPE);
            const formattedEvaluationTaskGroup = nonTeacherChosenQuestionsEvaluations.map((task) => {
                if (task.teacherId !== undefined && task.teacherId !== null) {
                    task.teacherChosenQuestionsEvaluation = teacherChosenQuestionsEvaluations.find(
                        (teacherChosenQuestionTask) => teacherChosenQuestionTask.teacherId === task.teacherId
                    );
                }
                return task;
            });

            return formattedEvaluationTaskGroup;
        }, [evaluationTaskGroup]);

        return (
            <ConfigPane
                title={evaluationPage.tableOfContentsTitle || "Table of Contents"}
                isOpen={isConfigPaneOpen}
                onIsOpenChange={configPaneIsOpenChangeHandler}
                isPinned={isConfigPanePinned}
                onIsPinnedChange={setIsConfigPanePinned}
                shouldShowPanelPreview
                data-testid='config-pane'
            >
                <div className={styles['config-pane-content-container']}>
                    {formatEvaluationTaskGroup().map((task) => (
                        <EvaluationTaskTicket
                            evaluationTask={task}
                            selectedEvaluationTask={selectedEvaluationTask}
                            evaluationTaskGroup={evaluationTaskGroup}
                            evaluationPage={evaluationPage}
                            setSelectedEvaluationTask={setSelectedEvaluationTask}
                            setShowCompletedPage={() => setCurrentMainContent(MainContent.CompletedPage)}
                            setShowSurveyPage={() => setCurrentMainContent(MainContent.SurveyPage)}
                        />
                    ))}
                </div>
            </ConfigPane>
        );
    };

    return (
        <div className={styles[`${containerStyle}`]}>
            <EvaluationExperienceHeader
                evaluationPage={evaluationPage}
                evaluations={evaluations}
                setSelectedEvaluation={setSelectedEvaluation}
                selectedEvaluation={evaluationTaskGroup}
                brandId={brandId}
                hashedStudentId={hashedStudentId}
            />
            <div className={styles['body-container']}>
                <EvaluationConfigPane />
                <LoadingSpinner
                    size="large"
                    background="hide"
                    show={currentMainContent === MainContent.SurveyPage && (isLoadingSurveyIFrame || isSubmissionTimeout)}
                    className={styles['survey-loading-spinner']}
                >
                    {isCustomTextEnabled && 
                     selectedEvaluationTask.evalType === COURSE_EVAL_TYPE &&
                     courseCustomText &&
                     courseCustomText.textContent &&
                     courseCustomText.textContent.length !== 0 && (
                        <CustomTextDisplay
                            textContent={courseCustomText.textContent}
                        />
                    )}
                    {isCustomTextEnabled && 
                     (selectedEvaluationTask.evalType === TEACHER_EVAL_TYPE ||
                     selectedEvaluationTask.evalType === TEACHER_CHOSEN_QUESTIONS_EVAL_TYPE ) && 
                     teacherCustomText &&
                     teacherCustomText.textContent &&
                     teacherCustomText.textContent.length !== 0 && (
                        <CustomTextDisplay
                            textContent={teacherCustomText.textContent}
                        />
                    )}
                    {currentMainContent === MainContent.IntitialRetakeScreen && (
                        <InitialRetakeScreen 
                            onClick={() => {
                                setCurrentMainContent(MainContent.SurveyPage);
                            }}
                            evaluationPage={evaluationPage}
                        />
                    )}
                    {currentMainContent === MainContent.SurveyPage && (
                        <iframe
                            className={styles['survey-iframe']}
                            src={surveyUrl}
                            onLoad={iFrameOnLoadHandler}
                        />
                    )}
                    {currentMainContent === MainContent.CompletedPage && (
                        <CompletedPage evaluationPage={evaluationPage} />
                    )}
                </LoadingSpinner>
            </div>
        </div>
    );
};

export default EvaluationExperience;
